import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import {
  createCorporation,
  updateCorporation,
  getCorporations,
  getSingleCorporation,
  getZipCode,
} from "../Api/ApiCalls";
import LoadingSpinner from "../common/Loading";
import { corporationValidationSchema } from "../common/Validation";
import log from "../core/log";

export const CreateCorporation = () => {
  const formOptions = { resolver: yupResolver(corporationValidationSchema) };
  const location = useLocation();
  const { id } = location.state || "";
  const [toasting, setToasting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [corporation, setCorporation] = useState([]);
  const [corporationData, setCorporationData] = useState();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
  } = useForm(formOptions);

  const onSubmit = async (data) => {
    setLoading(true);
    if (!corporationData) {
      try {
        const result = await createCorporation(data);
        if (result.status) {
          toast.success("Corporation created successfully");
          resetFields(data);
          manipulateCorpData(data, "ADD");
        } else {
          toast.info("Corporation code exist");
          reset({ ...data, corporation_code: "" });
        }
        setLoading(false);
      } catch (err) {
        console.log(err);
        log("Error while creating corporation data ", err);
        setLoading(false);
      }
    } else {
      try {
        delete data.id;
        await updateCorporation({
          id: corporationData.id,
          payload: data,
        });

        toast.success("Corporation Updated successfully", {
          toastId: "update",
          onClose: () => {
            setToasting(false);
          },
          onOpen: () => {
            setToasting(true);
          },
        });

        reset(data);
        manipulateCorpData(
          {
            id: corporationData.id,
            ...data,
          },
          "UPDATE"
        );
        setLoading(false);
      } catch (err) {
        console.log(err);
        log("Error while updating Corporation data ", err);
        setLoading(false);
      }
    }
  };

  const resetFields = (data) => {
    if (!data || !Object.keys(data).length) return;
    const tempObj = {};
    Object.keys(data).forEach((field) => {
      tempObj[field] = "";
    });
    reset(tempObj);
  };

  const getAllCorporation = async () => {
    try {
      setLoading(true);
      const data = await getCorporations();
      if (data) {
        setCorporation(data);
      }
      setLoading(false);
    } catch (err) {
      log(`Error while fetching organizations: `, err);
      setLoading(false);
    }
  };
  const manipulateCorpData = (data, method) => {
    if (!data || !method) return;
    setLoading(true);
    const tempCorpData = [...corporation];
    if (method === "ADD") {
      tempCorpData.push(data);
    } else if (method === "UPDATE") {
      const index = tempCorpData.findIndex((org) => org?.id == data?.id);
      if (index !== -1) tempCorpData[index] = data;
    } else if (method === "DELETE") {
      const index = tempCorpData.findIndex((org) => org?.id == data?.id);
      if (index !== -1) tempCorpData.splice(index, 1);
    }
    setCorporation(tempCorpData);
    setLoading(false);
  };

  const getCorporationData = async () => {
    setLoading(true);
    try {
      const result = await getSingleCorporation(id);
      if (result.status) {
        setCorporationData(result.data);
        reset(result.data);
      } else {
        toast.info(result.message);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      log("Error while fetching organization data ", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllCorporation();
  }, []);

  useEffect(() => {
    if (id) {
      getCorporationData();
    } else {
      resetFields(corporationData);
      setCorporationData();
    }
  }, [location]);

  const handleOnBlurZipCode = async (e) => {
    setLoading(true);
    try {
      const { value } = e.target;
      if (value) {
        const response = await getZipCode(value);
        if (response && response.status) {
          setValue("state", response.data.state);
          setValue("city", response.data.city);
        } else {
          toast.error("Please enter a valid zip code");
          setValue("state", "");
          setValue("city", "");
          setValue("pincode", "");
        }
      } else {
        setValue("state", "");
        setValue("city", "");
      }
      setLoading(false);
    } catch (err) {
      console.log("Error while validating zip code", err);
      setValue("state", "");
      setValue("pincode", "");
      setValue("city", "");
      setLoading(false);
      toast.error("Something went wrong . Please try again later.");
    }
  };

  return (
    <>
      <div className="grid grid-rows-1">
        <div className="flex overnpmflow-hidden">
          <div className="content relative flex-1 pb-2">
            <form onSubmit={handleSubmit(onSubmit)}>
              <h2 className="text-4xl text-[#96BF61] font-bold">
                {id ? "Edit Corporation" : "Create Corporation"}
              </h2>

              {loading && <LoadingSpinner />}
              <div className="grid grid-cols-12 mt-3">
                <div className="col-span-12 lg:col-span-12">
                  <div className=" shadow-md rounded">
                    <div className="bg-white rounded-[21px] p-10 mt-6">
                      <div className=" overflow-y-scroll">
                        <div className="flex flex-col sm:flex-row items-center">
                          <div className="flex-auto w-1/3 relative">
                            <div className="text-red-600 top-0 absolute my-4 block">
                              {errors?.corporation_name?.message}
                            </div>
                            <label
                              htmlFor="corporation_name"
                              className="text-black font-semibold text-sm mb-4 block"
                            >
                              Corporation name
                              <span className="text-red-600">*</span>
                            </label>
                            <input
                              id="corporation_name"
                              type="text"
                              {...register("corporation_name", {
                                required: true,
                              })}
                              className="py-3 bg-white shadow-md block w-full px-4 mt-0 bg-transparent border border-b-2 appearance-none focus:outline-none focus:ring-0 focus:border-gray-400 border-gray-200 rounded-md"
                              placeholder=" Corporation name"
                            />
                          </div>
                          <div className="flex-auto mx-8 w-1/3 relative">
                            <span className="text-red-600 top-0 absolute my-4 block">
                              {errors?.corporation_code?.message}
                            </span>
                            <label
                              htmlFor="corporation_code"
                              className="text-black font-semibold text-sm mb-4 block"
                            >
                              Corporation code
                              <span className="text-red-600">*</span>
                            </label>
                            <input
                              id="corporation_code"
                              {...register("corporation_code", {
                                required: true,
                              })}
                              className={`py-3 block shadow-md w-full px-4 mt-0  ${
                                corporationData ? "bg-gray-300" : "bg-white"
                              } border border-b-2 appearance-none w-full focus:outline-none focus:ring-0 focus:border-gray-400 border-gray-200 rounded-md`}
                              disabled={corporationData}
                              placeholder="Corporation code"
                            />
                          </div>
                          <div className="flex-auto w-1/3 c-select relative">
                            <span className="text-red-600 top-0 absolute my-4 block">
                              {errors?.country?.message}
                            </span>
                            <label
                              htmlFor="language"
                              className="text-black font-semibold text-sm mb-4 block"
                            >
                              Country
                              <span className="text-red-600">*</span>
                            </label>
                            <Controller
                              name={"country"}
                              control={control}
                              render={({ field: { onChange, value } }) => {
                                return (
                                  <>
                                    <select
                                      onChange={(e) => {
                                        onChange(e);
                                      }}
                                      id="country"
                                      value={value}
                                      label="Select country"
                                      variant="outlined"
                                      placeholder="Country"
                                      className={`py-3 shadow-md w-full block px-4 mt-0 bg-white border border-b-2 z-1 focus:outline-none focus:ring-0 focus:border-gray-400 border-gray-200 rounded-md ${
                                        value ? " focus-label" : ""
                                      }`}
                                    >
                                      <option value="">Select Country</option>
                                      <option value="usa">USA</option>
                                    </select>

                                    <div className="absolute right-2 top-[52px] pointer-events-none">
                                      <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          d="M12 17.414 3.293 8.707l1.414-1.414L12 14.586l7.293-7.293 1.414 1.414L12 17.414z"
                                          fill="#1D192B"
                                        ></path>
                                      </svg>
                                    </div>
                                  </>
                                );
                              }}
                              rules={{
                                required: {
                                  value: true,
                                  message: "Please select a country",
                                },
                              }}
                            />
                          </div>
                        </div>
                        <div className="mt-5 flex flex-col sm:flex-row items-center">
                          <div className="flex-auto w-1/3 c-select relative">
                            <span className="text-red-600 top-0 absolute my-4 block">
                              {errors?.pincode?.message}
                            </span>

                            <label
                              htmlFor="pincode"
                              className="text-black font-semibold text-sm mb-4 block"
                            >
                              Zip Code
                              <span className="text-red-600">*</span>
                            </label>
                            <input
                              id="pincode"
                              type="text"
                              {...register("pincode", {
                                required: true,
                                onBlur: (e) => {
                                  handleOnBlurZipCode(e);
                                },
                              })}
                              onKeyDown={(e) => {
                                e.keyCode === 13 && e.target.blur();
                              }}
                              className="py-3 bg-white shadow-md block w-full px-4 mt-0 bg-transparent border border-b-2 appearance-none focus:outline-none focus:ring-0 focus:border-gray-400 border-gray-200 rounded-md"
                              placeholder="Zip Code"
                            />
                          </div>
                          <div className="flex-auto w-1/3 mx-8 relative">
                            <span className="text-red-600 top-0 absolute my-4 block">
                              {errors?.state?.message}
                            </span>
                            <label
                              htmlFor="language"
                              className="text-black font-semibold text-sm mb-4 block"
                            >
                              State
                              <span className="text-red-600">*</span>
                            </label>
                            <input
                              id="state"
                              type="text"
                              {...register("state")}
                              className="py-3 bg-gray-300 shadow-md block w-full px-4 mt-0 border border-b-2 appearance-none focus:outline-none focus:ring-0 focus:border-gray-400 border-gray-200 rounded-md"
                              placeholder=" "
                              disabled
                            />
                          </div>
                          <div className="flex-auto  w-1/3 relative">
                            <span className="text-red-600 top-0 absolute my-4 block">
                              {errors?.city?.message}
                            </span>
                            <label
                              htmlFor="language"
                              className="text-black font-semibold text-sm mb-4 block"
                            >
                              City
                              <span className="text-red-600">*</span>
                            </label>
                            <input
                              id="city"
                              type="text"
                              {...register("city")}
                              className="py-3 bg-gray-300 shadow-md block w-full px-4 mt-0 border border-b-2 appearance-none focus:outline-none focus:ring-0 focus:border-gray-400 border-gray-200 rounded-md"
                              placeholder=" "
                              disabled
                            />
                          </div>
                        </div>
                        <div className="mt-5 flex flex-col sm:flex-row items-center">
                          <div className="flex-auto w-1/3 c-select relative">
                            <span className="text-red-600 top-0 absolute my-4 block">
                              {errors?.address_line_one?.message}
                            </span>
                            <label
                              htmlFor="address_line_one"
                              className="text-black font-semibold text-sm mb-4 block"
                            >
                              Address Line 1
                              <span className="text-red-600">*</span>
                            </label>
                            <input
                              id="address_line_one"
                              type="text"
                              placeholder="Address Line 1"
                              className="py-3 shadow-md bg-white block w-full px-4 mt-0 bg-transparent border border-b-2 appearance-none focus:outline-none focus:ring-0 focus:border-gray-400 border-gray-200 rounded-md"
                              {...register("address_line_one")}
                            />
                          </div>
                          <div className="flex-auto w-1/3 mx-8 relative">
                            <label
                              htmlFor="address_line_two"
                              className="text-black font-semibold text-sm mb-4 block"
                            >
                              Address Line 2
                            </label>
                            <input
                              id="address_line_two"
                              type="text"
                              placeholder="Address Line 2"
                              className="py-3 shadow-md bg-white block w-full px-4 mt-0 bg-transparent border border-b-2 appearance-none focus:outline-none focus:ring-0 focus:border-gray-400 border-gray-200 rounded-md"
                              {...register("address_line_two")}
                            />
                          </div>
                          <div className="flex-auto w-1/3  c-select relative">
                            <label
                              htmlFor="address_line_three"
                              className="text-black font-semibold text-sm mb-4 block"
                            >
                              Address Line 3
                            </label>
                            <input
                              id="address_line_three"
                              type="text"
                              placeholder="Address Line 3"
                              className="py-3 shadow-md bg-white block w-full px-4 mt-0 bg-transparent border border-b-2 appearance-none focus:outline-none focus:ring-0 focus:border-gray-400 border-gray-200 rounded-md"
                              {...register("address_line_three")}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="pt-12 text-right">
                        <button
                          disabled={toasting}
                          type="form"
                          className="px-4 py-2 text-lg text-white transition-all duration-150 ease-linear rounded-md shadow outline-none font-semibold bg-[#024C85] hover:bg-[#134d86] hover:shadow-lg focus:outline-none"
                        >
                          {corporationData
                            ? "Update Corporation"
                            : "Create Corporation"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
